import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Row,
  Col,
  message,
} from 'antd';
import {
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupportTickets, addSupportTicket, updateSupportTicket } from '../../api/support';
import type { SupportTicket } from '../../api/support';
import SupportTicketsTable from '../../components/features/support/SupportTicketsTable';
import SupportTicketModal from '../../components/features/support/SupportTicketModal';

const { Title } = Typography;
const PAGE_SIZE = 10;

type LastDocs = { [page: number]: any };

type SupportTicketModalForm = Omit<SupportTicket, 'id'>;

const Support: React.FC = () => {
  const { t } = useTranslation(['support', 'common']);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [editingTicket, setEditingTicket] = useState<SupportTicket | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [lastDocs, setLastDocs] = useState<LastDocs>({});
  const [hasNext, setHasNext] = useState<boolean>(false);

  const { data = { tickets: [], lastDoc: undefined }, isLoading } = useQuery<{ tickets: SupportTicket[]; lastDoc?: any }>({
    queryKey: ['supportTickets', page, lastDocs[page - 1]],
    queryFn: async ({ queryKey }) => {
      const [_key, _page] = queryKey as [string, number, any];
      return getSupportTickets();
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    if (data.lastDoc) {
      setLastDocs(prev => ({ ...prev, [page]: data.lastDoc }));
      setHasNext(!!data.lastDoc && data.tickets.length === PAGE_SIZE);
    }
  }, [data, page]);

  const tickets: SupportTicket[] = data.tickets;

  const addMutation = useMutation({
    mutationFn: (values: SupportTicketModalForm) => addSupportTicket(values),
    onSuccess: () => {
      message.success('Support ticket added');
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      setIsModalOpen(false);
      setEditingTicket(null);
    },
    onError: () => message.error('Failed to add support ticket'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<SupportTicketModalForm>) => updateSupportTicket(id, data),
    onSuccess: () => {
      message.success('Support ticket updated');
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      setIsModalOpen(false);
      setEditingTicket(null);
    },
    onError: () => message.error('Failed to update support ticket'),
  });

  const handleAdd = useCallback(() => {
    setEditingTicket(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((ticket: SupportTicket) => {
    setEditingTicket(ticket);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((ticket: SupportTicket) => {
    navigate(`/support/${ticket.id}`, { state: { ticket } });
  }, [navigate]);

  const handleModalSuccess = useCallback((values?: SupportTicketModalForm) => {
    if (editingTicket) {
      updateMutation.mutate({ ...editingTicket, ...values });
    } else if (values) {
      addMutation.mutate(values);
    }
  }, [editingTicket, addMutation, updateMutation]);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingTicket(null);
  }, []);

  const pagination = useMemo(() => ({
    current: page,
    pageSize: PAGE_SIZE,
    total: hasNext ? (page + 1) * PAGE_SIZE : page * PAGE_SIZE,
    onChange: (newPage: number) => setPage(newPage),
  }), [page, hasNext]);

  return (
    <div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4}>{t('support:title')}</Title>
            </Col>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                {t('support:add')}
              </Button>
            </Col>
          </Row>
          <SupportTicketsTable
            tickets={tickets}
            loading={isLoading}
            pagination={pagination}
            onEdit={handleEdit}
            onView={handleView}
            onTableChange={pagination.onChange}
          />
        </Space>
      </Card>
      <SupportTicketModal
        open={isModalOpen}
        ticket={editingTicket}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
        destroyOnHidden
      />
    </div>
  );
};

export default Support; 